import { motion, AnimatePresence } from "framer-motion";
import { Circle, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

import { ElegantShape } from "@/components/ui/ElegantShape";

const slides = [
    {
        badge: "SpendlyPay",
        title1: "Welcome to",
        title2: "SpendlyPay",
        description: "Your smart companion for managing finances effortlessly."
    },
    {
        badge: "Smart Budget",
        title1: "Smart Budget",
        title2: "Categories",
        description: "Organize your spending with customizable budget categories based on your salary."
    },
    {
        badge: "UPI Payments",
        title1: "Instant UPI",
        title2: "Payments",
        description: "Make quick payments directly from your budget categories with saved UPI IDs."
    },
    {
        badge: "Analytics",
        title1: "Track Your",
        title2: "Spending",
        description: "Visualize your financial health with detailed analytics and insights."
    }
];

function Onboarding() {
    const navigate = useNavigate();
    const [currentSlide, setCurrentSlide] = useState(0);

    const handleNext = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(prev => prev + 1);
        } else {
            handleGetStarted();
        }
    };

    const handlePrevious = () => {
        if (currentSlide > 0) {
            setCurrentSlide(prev => prev - 1);
        }
    };

    const handleGetStarted = () => {
        localStorage.setItem('onboardingCompleted', 'true');
        navigate('/login');
    };

    const fadeUpVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                delay: 0.2 + i * 0.1,
                ease: [0.25, 0.4, 0.25, 1],
            },
        }),
        exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
    };

    const slide = slides[currentSlide];

    return (
        <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#030303]">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/[0.05] via-transparent to-rose-500/[0.05] blur-3xl" />

            <div className="absolute inset-0 overflow-hidden">
                <ElegantShape
                    delay={0.3}
                    width={600}
                    height={140}
                    rotate={12}
                    gradient="from-indigo-500/[0.15]"
                    className="left-[-10%] md:left-[-5%] top-[15%] md:top-[20%]"
                />

                <ElegantShape
                    delay={0.5}
                    width={500}
                    height={120}
                    rotate={-15}
                    gradient="from-rose-500/[0.15]"
                    className="right-[-5%] md:right-[0%] top-[70%] md:top-[75%]"
                />

                <ElegantShape
                    delay={0.4}
                    width={300}
                    height={80}
                    rotate={-8}
                    gradient="from-violet-500/[0.15]"
                    className="left-[5%] md:left-[10%] bottom-[5%] md:bottom-[10%]"
                />

                <ElegantShape
                    delay={0.6}
                    width={200}
                    height={60}
                    rotate={20}
                    gradient="from-amber-500/[0.15]"
                    className="right-[15%] md:right-[20%] top-[10%] md:top-[15%]"
                />

                <ElegantShape
                    delay={0.7}
                    width={150}
                    height={40}
                    rotate={-25}
                    gradient="from-cyan-500/[0.15]"
                    className="left-[20%] md:left-[25%] top-[5%] md:top-[10%]"
                />
            </div>

            <div className="relative z-10 container mx-auto px-4 md:px-6">
                <div className="max-w-3xl mx-auto text-center">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentSlide}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            variants={{
                                hidden: { opacity: 0 },
                                visible: { opacity: 1 },
                                exit: { opacity: 0 }
                            }}
                        >


                            <motion.div
                                custom={1}
                                variants={fadeUpVariants}
                            >
                                <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold mb-6 md:mb-8 tracking-tight">
                                    <span className="bg-clip-text text-transparent bg-gradient-to-b from-white to-white/80">
                                        {slide.title1}
                                    </span>
                                    <br />
                                    <span
                                        className={cn(
                                            "bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 via-white/90 to-rose-300 "
                                        )}
                                    >
                                        {slide.title2}
                                    </span>
                                </h1>
                            </motion.div>

                            <motion.div
                                custom={2}
                                variants={fadeUpVariants}
                            >
                                <p className="text-base sm:text-lg md:text-xl text-white/40 mb-8 leading-relaxed font-light tracking-wide max-w-xl mx-auto px-4">
                                    {slide.subtitle ? (
                                        <span className="block mb-2 font-medium text-indigo-300">
                                            {slide.subtitle}
                                        </span>
                                    ) : null}
                                    {slide.description}
                                </p>
                            </motion.div>
                        </motion.div>
                    </AnimatePresence>

                    {/* Navigation Buttons */}
                    <div className="flex items-center justify-center gap-4 mt-8">
                        <motion.button
                            initial={{ opacity: 0 }}
                            animate={{ opacity: currentSlide === 0 ? 0.3 : 1 }}
                            disabled={currentSlide === 0}
                            onClick={handlePrevious}
                            className="group relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.03] border border-white/[0.08] text-white transition-all hover:bg-white/[0.08] active:scale-95 disabled:cursor-not-allowed disabled:hover:bg-white/[0.03]"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </motion.button>

                        <div className="flex gap-2">
                            {slides.map((_, index) => (
                                <div
                                    key={index}
                                    className={cn(
                                        "w-1.5 h-1.5 rounded-full transition-all duration-300",
                                        index === currentSlide ? "bg-indigo-500 w-6" : "bg-white/20"
                                    )}
                                />
                            ))}
                        </div>

                        <motion.button
                            onClick={handleNext}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="group relative inline-flex items-center gap-2 px-6 py-3 bg-white text-black rounded-full text-base font-medium transition-colors"
                        >
                            {currentSlide === slides.length - 1 ? 'Get Started' : 'Next'}
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </motion.button>
                    </div>
                </div>
            </div>

            <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-[#030303]/80 pointer-events-none" />
        </div>
    );
}

export default Onboarding;
